import React, { useRef } from 'react';
import clsx from 'clsx';
import {
  createStyles,
  makeStyles,
  Theme,
  IconButton,
  Popper,
  List,
} from '@material-ui/core';

import { EllipsisVIcon } from '../icon/solid';
import { useDropdown } from '../../hooks';

const useStyles = makeStyles(({ palette, colors }: Theme) => createStyles({
  root: {
    display: 'inline-block',
  },
  arrowTop: {
    position: 'absolute',
    display: 'block',
    width: '2rem',
    height: '1rem',
    top: '0',
    right: '0.5rem',
    '&::after': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderBottomColor: '#fff',
      borderWidth: '0 1rem 1rem 1rem',
      borderStyle: 'solid',
      top: '1px',
    },
    '&::before': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderBottomColor: colors.border,
      borderWidth: '0 1rem 1rem 1rem',
      borderStyle: 'solid',
      top: '0',
    },
  },
  arrowBottom: {
    position: 'absolute',
    display: 'block',
    width: '2rem',
    height: '1rem',
    bottom: 0,
    right: '0.5rem',
    '&::after': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderTopColor: '#FFF',
      borderWidth: '1rem 1rem 0 1rem',
      borderStyle: 'solid',
      top: '0',
    },
    '&::before': {
      position: 'absolute',
      display: 'block',
      content: "''",
      borderColor: 'transparent',
      borderTopColor: colors.border,
      borderWidth: '1rem 1rem 0 1rem',
      borderStyle: 'solid',
      top: '1px',
    },
  },
  menuButton: {
    color: palette.text.primary,
    '&:focus': {
      outline: 'none',
    },
  },
  icon: {
    color: colors.selected,
    width: 16,
    height: 16,
  },
  menuIcon: {
    padding: '3px 0',
    color: palette.icons.default,
  },
  openedMenuIcon: {
    color: palette.menuAction.hover,
  },
  menu: {
    minWidth: 150,
    padding: '5px',
    zIndex: 2147483100,
  },
  menuContainerBottom: {
    border: `1px solid ${colors.border}`,
    padding: '5px',
    background: '#fff',
    margin: '0.6rem 0 0 0',
    boxShadow: '1px 1px 10px 1px rgba(0,0,0,0.12)',
  },
  menuContainerTop: {
    border: `1px solid ${colors.border}`,
    padding: '5px',
    background: '#fff',
    margin: '0 0 0.6rem 0',
    boxShadow: '1px 1px 10px 1px rgba(0,0,0,0.12)',
  },
}), { name: 'ActionCell' });

type Props = {
  className?: string;
  children: React.ReactNode | React.ReactNodeArray;
};

const stopPropagation: React.MouseEventHandler = (e) => {
  e.stopPropagation();
};

const ActionCell = ({ children, className }: Props) => {
  const menuRef = useRef<HTMLButtonElement | null>(null);

  if (children && Array.isArray(children)) {
    children = children.filter((item) => item !== false);
    if (Array.isArray(children) && children.length === 0) {
      children = null;
    }
  }

  const classes = useStyles();

  const { isOpened, open, close, toggle } = useDropdown();

  if (!children) {
    return null;
  }

  return (
    <div className={clsx(classes.root, className)} onClick={stopPropagation}>
      <IconButton
        tabIndex={-1}
        onMouseEnter={open}
        onMouseLeave={close}
        onClick={toggle}
        className={classes.menuButton}
        ref={menuRef}
      >
        <EllipsisVIcon
          className={clsx(classes.menuIcon, {
            [classes.openedMenuIcon]: isOpened,
          })}
        />
      </IconButton>
      <Popper
        hidden={!isOpened}
        anchorEl={menuRef.current}
        open={isOpened}
        onMouseEnter={open}
        onMouseLeave={close}
        onClick={close}
        placement="bottom-end"
        className={classes.menu}
        keepMounted
        modifiers={{
          flip: {
            enabled: true,
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'window',
          },
        }}
      >
        {({ placement }) => (
          <div
            className={(placement.indexOf('bottom') !== -1) ? classes.menuContainerBottom : classes.menuContainerTop}
          >
            <i className={(placement.indexOf('bottom') !== -1) ? classes.arrowTop : classes.arrowBottom} />
            <List>
              {children}
            </List>
          </div>
        )}

      </Popper>
    </div>
  );
};

export default ActionCell;
